// Dialog para recortar y redimensionar foto antes de subir.
// Sin dependencias externas — usa HTML5 Canvas.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Button, Typography, Slider, CircularProgress, Alert,
} from '@mui/material';
import CropIcon from '@mui/icons-material/Crop';
import CloseIcon from '@mui/icons-material/Close';

// Configuración de salida
const OUT_W = 600;
const OUT_H = 750; // aspect 4:5 (foto carnet)
const QUALITY = 0.85;
const VIEW_W = 360;
const VIEW_H = 450;

export default function FotoCropDialog({ open, file, onClose, onConfirm }) {
    const [imgSrc, setImgSrc] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
    const [dragging, setDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [processing, setProcessing] = useState(false);
    const imgRef = useRef(null);

    // Cargar imagen al abrir
    useEffect(() => {
        if (!file || !open) {
            setImgSrc(null);
            setZoom(1);
            setPos({ x: 0, y: 0 });
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => setImgSrc(e.target.result);
        reader.readAsDataURL(file);
    }, [file, open]);

    const onImgLoad = (e) => {
        const img = e.target;
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        setImgSize({ w, h });
        // Auto-fit: zoom para que cubra la viewport con su lado más corto
        const fitZoom = Math.max(VIEW_W / w, VIEW_H / h);
        setZoom(fitZoom);
        setPos({
            x: (VIEW_W - w * fitZoom) / 2,
            y: (VIEW_H - h * fitZoom) / 2,
        });
    };

    const handleMouseDown = (e) => {
        setDragging(true);
        setDragStart({ x: e.clientX - pos.x, y: e.clientY - pos.y });
    };
    const handleMouseMove = useCallback((e) => {
        if (!dragging) return;
        setPos({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }, [dragging, dragStart]);
    const handleMouseUp = () => setDragging(false);

    useEffect(() => {
        if (dragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [dragging, handleMouseMove]);

    const handleZoomChange = (_, v) => {
        // Mantener el centro al hacer zoom
        const cx = VIEW_W / 2 - pos.x;
        const cy = VIEW_H / 2 - pos.y;
        const newPos = {
            x: VIEW_W / 2 - cx * (v / zoom),
            y: VIEW_H / 2 - cy * (v / zoom),
        };
        setZoom(v);
        setPos(newPos);
    };

    const handleConfirm = async () => {
        if (!imgRef.current) return;
        setProcessing(true);
        try {
            // Render a canvas de salida
            const canvas = document.createElement('canvas');
            canvas.width = OUT_W;
            canvas.height = OUT_H;
            const ctx = canvas.getContext('2d');
            // Fondo blanco
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, OUT_W, OUT_H);

            // Mapeo: viewport (VIEW_W x VIEW_H) → output (OUT_W x OUT_H)
            const scaleX = OUT_W / VIEW_W;
            const scaleY = OUT_H / VIEW_H;
            // Posición de la imagen en el viewport: pos (px), tamaño zoom*imgSize
            // En canvas final: drawImage de la imagen original con el factor escalado
            const drawW = imgSize.w * zoom * scaleX;
            const drawH = imgSize.h * zoom * scaleY;
            const drawX = pos.x * scaleX;
            const drawY = pos.y * scaleY;
            ctx.drawImage(imgRef.current, drawX, drawY, drawW, drawH);

            // Convertir a blob (JPEG comprimido)
            const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', QUALITY));
            if (!blob) throw new Error('No se pudo procesar la imagen');
            // Convertir blob a File para mantener nombre
            const newFile = new File([blob], (file?.name || 'foto').replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' });
            await onConfirm(newFile);
            onClose();
        } catch (e) {
            alert('Error procesando imagen: ' + e.message);
        }
        setProcessing(false);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '4px' } }}>
            <DialogTitle sx={{ bgcolor: '#0277bd', color: '#fff', fontSize: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CropIcon sx={{ fontSize: 20 }} />
                Recortar foto
            </DialogTitle>
            <DialogContent sx={{ pt: 2, mt: 1 }}>
                {!imgSrc ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                ) : (
                    <Box>
                        <Alert severity="info" sx={{ mb: 1.5, borderRadius: '4px', fontSize: '0.75rem', py: 0.5 }}>
                            Arrastra para mover · Usa el deslizador para zoom · Formato 4:5 (foto carnet)
                        </Alert>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Box sx={{
                                width: VIEW_W, height: VIEW_H,
                                overflow: 'hidden', position: 'relative',
                                bgcolor: '#000', cursor: dragging ? 'grabbing' : 'grab',
                                border: '2px solid #0277bd', borderRadius: '4px',
                                userSelect: 'none',
                            }} onMouseDown={handleMouseDown}>
                                {imgSrc && (
                                    <img
                                        ref={imgRef}
                                        src={imgSrc}
                                        alt="recortar"
                                        onLoad={onImgLoad}
                                        draggable={false}
                                        style={{
                                            position: 'absolute',
                                            left: pos.x, top: pos.y,
                                            width: imgSize.w * zoom,
                                            height: imgSize.h * zoom,
                                            pointerEvents: 'none',
                                            maxWidth: 'none',
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="caption" sx={{ minWidth: 50 }}>Zoom</Typography>
                            <Slider
                                value={zoom}
                                onChange={handleZoomChange}
                                min={0.1} max={3} step={0.05}
                                size="small"
                                sx={{ color: '#0277bd' }}
                            />
                            <Typography variant="caption" sx={{ minWidth: 40 }}>{(zoom * 100).toFixed(0)}%</Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block', textAlign: 'center', mt: 1 }}>
                            Salida: {OUT_W}×{OUT_H}px · JPEG calidad {Math.round(QUALITY * 100)}%
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} startIcon={<CloseIcon />} sx={{ textTransform: 'none' }} disabled={processing}>Cancelar</Button>
                <Button onClick={handleConfirm} variant="contained" startIcon={processing ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CropIcon />}
                    disabled={!imgSrc || processing}
                    sx={{ bgcolor: '#0277bd', textTransform: 'none', borderRadius: '4px', '&:hover': { bgcolor: '#01579b' } }}>
                    {processing ? 'Procesando...' : 'Aplicar y subir'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
